import { OntologyType } from "core/model/utils/ontologies";
import { useGetOntologies } from "core/model/utils/ontologies/hooks";
import ConnectedSelectInput, {
  ConnectedSelectInputProps,
  SelectInput,
  SelectInputProps,
} from "ds_legacy/components/SelectInput";

type OntologyOption = { id: number; label: string; value: number };

type OntologyProps = {
  exclude?: Array<number>;
  include?: Array<number>;
  shouldSort?: boolean;
  type: OntologyType;
};

export function useOntologyOptions({
  exclude,
  include,
  shouldSort,
  type,
}: OntologyProps): OntologyOption[] {
  const getOntologies = useGetOntologies();
  let values = getOntologies({ sort: shouldSort, type });

  if (exclude != null && Array.isArray(exclude)) {
    values = values.filter((v) => !exclude.includes(v.value));
  }

  if (include != null && Array.isArray(include)) {
    values = values.filter((v) => include.includes(v.value));
  }

  return values.map(({ label, value }) => ({ id: value, label, value }));
}

export function OntologySelectInput<
  Multiple extends boolean | undefined = undefined,
  Nullable extends boolean | undefined = undefined,
>({
  exclude,
  include,
  shouldSort,
  type,
  ...props
}: Omit<SelectInputProps<OntologyOption, Multiple, Nullable>, "options"> &
  OntologyProps) {
  const options = useOntologyOptions({
    exclude,
    include,
    shouldSort,
    type,
  });

  return (
    <SelectInput<OntologyOption, Multiple, Nullable>
      {...props}
      options={options}
    />
  );
}

export default function ConnectedOntologySelectInput<
  Multiple extends boolean | undefined = undefined,
  Clearable extends boolean | undefined = undefined,
>({
  exclude,
  include,
  shouldSort,
  type,
  ...props
}: Omit<
  ConnectedSelectInputProps<OntologyOption, Multiple, Clearable>,
  "options"
> &
  OntologyProps) {
  const options = useOntologyOptions({
    exclude,
    include,
    shouldSort,
    type,
  });

  return (
    <ConnectedSelectInput<OntologyOption, Multiple, Clearable>
      {...props}
      options={options}
    />
  );
}
